import { useState } from 'react';
import { Briefcase, Users, Play, RotateCcw } from 'lucide-react';
import { useAllProfiles } from '../hooks/use-sales';
import { useAssignments } from '../hooks/use-assignments';
import { useRunMatching, useMatches } from '../hooks/use-matches';
import { useT } from '../lib/i18n';
import { useCurrentUser } from '../hooks/use-current-user';

const MAX_ASSIGNMENTS = 10;

export function SalesPage() {
  const tFn = useT();
  const { canAccessSales } = useCurrentUser();
  const [selectedProfileIds, setSelectedProfileIds] = useState<Set<string>>(new Set());
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<string>>(new Set());
  const [lastRunIds, setLastRunIds] = useState<{
    profiles: string[];
    assignments: string[];
  } | null>(null);

  const { data: profilesResp, isLoading: loadingProfiles } = useAllProfiles();
  const { data: assignmentsResp, isLoading: loadingAssignments } = useAssignments('active');
  const { data: matchesResp } = useMatches(1, 200);
  const runMatching = useRunMatching();

  if (!canAccessSales) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-lg font-semibold">Access Restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have permission to view this page. Contact your admin for access.
        </p>
      </div>
    );
  }

  const profiles = profilesResp?.data ?? [];
  const assignments = assignmentsResp?.data ?? [];
  const allMatches = matchesResp?.data ?? [];

  // Filter matches to only those from the last run
  const resultsMatches = lastRunIds
    ? allMatches.filter(
        (m) =>
          lastRunIds.profiles.includes(m.profileId) &&
          lastRunIds.assignments.includes(m.assignmentId)
      )
    : [];

  const sortedResults = [...resultsMatches].sort((a, b) => {
    const scoreA = Number(a.heuristicScore ?? 0);
    const scoreB = Number(b.heuristicScore ?? 0);
    return scoreB - scoreA;
  });

  function toggleProfile(id: string) {
    setSelectedProfileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAssignment(id: string) {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_ASSIGNMENTS) {
        next.add(id);
      }
      return next;
    });
  }

  function selectAllProfiles() {
    setSelectedProfileIds(new Set(profiles.map((p) => p.id)));
  }

  function clearProfiles() {
    setSelectedProfileIds(new Set());
  }

  async function handleRunMatching() {
    const profileIds = [...selectedProfileIds];
    const assignmentIds = [...selectedAssignmentIds];
    await runMatching.mutateAsync({ profileIds, assignmentIds });
    setLastRunIds({ profiles: profileIds, assignments: assignmentIds });
  }

  const canRun = selectedProfileIds.size > 0 && selectedAssignmentIds.size > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{tFn('nav.sales')}</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            {selectedProfileIds.size} profile{selectedProfileIds.size !== 1 ? 's' : ''} x{' '}
            {selectedAssignmentIds.size} assignment{selectedAssignmentIds.size !== 1 ? 's' : ''}{' '}
            selected
          </span>
          <button
            onClick={handleRunMatching}
            disabled={!canRun || runMatching.isPending}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
          >
            {runMatching.isPending ? (
              <>
                <RotateCcw className="h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run Matching
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Profiles column */}
        <div className="rounded-lg border">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Profiles</span>
              {profiles.length > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {profiles.length}
                </span>
              )}
            </div>
            <div className="flex gap-2 text-xs">
              <button onClick={selectAllProfiles} className="text-primary hover:underline">
                All
              </button>
              <span className="text-muted-foreground">/</span>
              <button onClick={clearProfiles} className="text-muted-foreground hover:underline">
                None
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loadingProfiles ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
            ) : profiles.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No profiles found
              </div>
            ) : (
              profiles.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-0 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selectedProfileIds.has(p.id)}
                    onChange={() => toggleProfile(p.id)}
                    className="h-4 w-4 rounded border-border"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{p.name}</p>
                    {p.email && <p className="truncate text-xs text-muted-foreground">{p.email}</p>}
                  </div>
                  {p.seniority && (
                    <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">
                      {p.seniority}
                    </span>
                  )}
                </label>
              ))
            )}
          </div>
        </div>

        {/* Assignments column */}
        <div className="rounded-lg border">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Assignments</span>
              {assignments.length > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {assignments.length}
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {selectedAssignmentIds.size}/{MAX_ASSIGNMENTS} max
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loadingAssignments ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
            ) : assignments.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No active assignments
              </div>
            ) : (
              assignments.map((a) => {
                const isAtLimit =
                  !selectedAssignmentIds.has(a.id) && selectedAssignmentIds.size >= MAX_ASSIGNMENTS;
                return (
                  <label
                    key={a.id}
                    className={`flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-0 hover:bg-muted/50 ${isAtLimit ? 'opacity-40' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAssignmentIds.has(a.id)}
                      onChange={() => toggleAssignment(a.id)}
                      disabled={isAtLimit}
                      className="h-4 w-4 rounded border-border"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{a.title}</p>
                      {a.location && (
                        <p className="truncate text-xs text-muted-foreground">{a.location}</p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Results table */}
      {lastRunIds && (
        <div className="rounded-lg border">
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">
              Results
              {sortedResults.length > 0 && (
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                  {sortedResults.length} matches
                </span>
              )}
            </h2>
          </div>
          {sortedResults.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {runMatching.isPending
                ? 'Running matching...'
                : 'No results yet. Run matching above.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                      Profile
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                      Assignment
                    </th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedResults.map((m) => (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{m.profileName}</span>
                        {m.profileEmail && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({m.profileEmail})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{m.assignmentTitle}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            Number(m.heuristicScore ?? 0) >= 7
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : Number(m.heuristicScore ?? 0) >= 5
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          }`}
                        >
                          {Number(m.heuristicScore ?? 0).toFixed(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
